import React, { useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

import type { PartnerShop } from '../PartnerCreatePage';
import MainImagesUploadSection from './MainImagesUploadSection';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface BasicInfoStepProps {
  partnerShop: PartnerShop;
  onChangePartnerShop: (nextPartnerShop: PartnerShop) => void;
}

const SHOP_CATEGORY_OPTIONS = [
  { label: '예복', value: '예복' },
  { label: '드레스', value: '드레스' },
  { label: '스튜디오', value: '스튜디오' },
  { label: '메이크업', value: '메이크업' },
  { label: '예물', value: '예물' },
  { label: '한복', value: '한복' },
  { label: '베뉴', value: '베뉴' },
  { label: '혼수', value: '혼수' },
  { label: '기타', value: '기타' },
];

const STATUS_OPTIONS = [
  { label: '작성 중', value: 'DRAFT' },
  { label: '노출', value: 'ACTIVE' },
  { label: '숨김', value: 'HIDDEN' },
];

const CERTIFIED_OPTIONS = [
  { label: '인증업체', value: 1 },
  { label: '일반업체', value: 0 },
];

function normalizePartnerShopFormValues(
  partnerShop: PartnerShop,
  formValues: Partial<PartnerShop>
): PartnerShop {
  const lat =
    formValues.lat === undefined
      ? formValues.latitude === undefined
        ? partnerShop.lat ?? partnerShop.latitude
        : formValues.latitude
      : formValues.lat;
  const lon =
    formValues.lon === undefined
      ? formValues.longitude === undefined
        ? partnerShop.lon ?? partnerShop.longitude
        : formValues.longitude
      : formValues.lon;

  return {
    ...partnerShop,
    ...formValues,

    badgesJson: formValues.badgesJson || [],
    verifiedReasonsJson: formValues.verifiedReasonsJson || [],
    featuresJson: formValues.featuresJson || [],
    priceItemsJson: formValues.priceItemsJson || [],
    faqJson: formValues.faqJson || [],
    noticeJson: formValues.noticeJson || [],
    promotionBenefitsJson: formValues.promotionBenefitsJson || [],

    lat,
    lon,
    latitude: lat,
    longitude: lon,

    isCertified:
      formValues.isCertified === undefined
        ? partnerShop.isCertified
        : formValues.isCertified,

    sortOrder:
      formValues.sortOrder === undefined ? partnerShop.sortOrder : formValues.sortOrder,

    heroImagesJson: partnerShop.heroImagesJson,
  };
}

export default function BasicInfoStep({
  partnerShop,
  onChangePartnerShop,
}: BasicInfoStepProps) {
  const [form] = Form.useForm<PartnerShop>();
  const watchedDisplayName = Form.useWatch('displayName', form);
  const naverMapSearchKeyword = String(
    watchedDisplayName || partnerShop.displayName || partnerShop.shopName || ''
  ).trim();

  useEffect(() => {
    form.setFieldsValue({
      ...partnerShop,
      lat: partnerShop.lat ?? partnerShop.latitude,
      lon: partnerShop.lon ?? partnerShop.longitude,
    });
  }, [form, partnerShop]);

  return (
    <Card>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            기본 정보 입력
          </Title>
          <Text type="secondary">
            고객에게 노출될 업체 소개, 대표 이미지, 가격, 혜택, 방문 안내를 입력합니다.
          </Text>
        </div>

        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <MainImagesUploadSection
            value={partnerShop.heroImagesJson}
            onChange={(nextHeroImagesJson) => {
              onChangePartnerShop({
                ...partnerShop,
                heroImagesJson: nextHeroImagesJson,
              });
            }}
          />

          <Form<PartnerShop>
            form={form}
            layout="vertical"
            initialValues={partnerShop}
            onValuesChange={(_, allValues) => {
              onChangePartnerShop(
                normalizePartnerShopFormValues(partnerShop, allValues)
              );
            }}
          >
            <Card size="small" title="상단 노출 정보">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="노출 이름"
                    name="displayName"
                    rules={[
                      { required: true, message: '노출 이름을 입력해 주세요.' },
                    ]}
                  >
                    <Input placeholder="예: 헤리츠테일러 청담본점" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  {/* <Form.Item
                    label="실제 업체명"
                    name="shopName"
                    rules={[
                      { required: true, message: '실제 업체명을 입력해 주세요.' },
                    ]}
                  >
                    <Input placeholder="예: 헤리츠테일러" />
                  </Form.Item> */}
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="주소에 사용할 이름"
                    name="shopSlug"
                    rules={[
                      {
                        required: true,
                        message: '주소에 사용할 이름을 입력해 주세요.',
                      },
                    ]}
                    extra="영문, 숫자, 하이픈 조합을 권장합니다."
                  >
                    <Input placeholder="예: heritz-tailor-cheongdam" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="분야"
                    name="shopCategory"
                    rules={[{ required: true, message: '분야를 선택해 주세요.' }]}
                  >
                    <Select options={SHOP_CATEGORY_OPTIONS} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="지점명" name="branchName">
                    <Input placeholder="예: 청담본점" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="지역" name="areaName">
                    <Input placeholder="예: 청담동" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={16}>
                  <Form.Item label="한 줄 소개" name="shortDescription">
                    <Input placeholder="예: 40년 헤리티지의 프리미엄 맞춤 예복 전문점" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="인증 여부" name="isCertified">
                    <Select options={CERTIFIED_OPTIONS} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="노출 상태" name="status">
                    <Select options={STATUS_OPTIONS} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="노출 순서" name="sortOrder">
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card style={{marginTop: 12}} size="small" title="브랜드 소개">
              <Form.Item label="Brand Story" name="brandStory">
                <TextArea
                  rows={7}
                  placeholder="업체의 분위기, 강점, 상담 방식, 고객에게 전달하고 싶은 이야기를 입력해 주세요."
                />
              </Form.Item>
            </Card>

            {/* <Card size="small" title="상단 배지" >
              <Form.List name="badgesJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`배지 ${field.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="문구"
                              name={[field.name, 'title']}
                              rules={[
                                { required: true, message: '문구를 입력해 주세요.' },
                              ]}
                            >
                              <Input placeholder="예: 다이렉트 인증" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={12}>
                            <Form.Item label="설명" name={[field.name, 'description']}>
                              <Input placeholder="예: 실제 후기와 상담 품질을 기준으로 검토했습니다." />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={4}>
                            <Form.Item label="순서" name={[field.name, 'sortOrder']}>
                              <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          title: '',
                          description: '',
                          sortOrder: fields.length + 1,
                          isPublished: 1,
                        })
                      }
                    >
                      배지 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card> */}

            {/* <Card size="small" title="검증 포인트">
              <Form.List name="verifiedReasonsJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`검증 포인트 ${field.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="제목"
                              name={[field.name, 'title']}
                              rules={[
                                { required: true, message: '제목을 입력해 주세요.' },
                              ]}
                            >
                              <Input placeholder="예: 실제 후기 기반 검증" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={16}>
                            <Form.Item label="설명" name={[field.name, 'description']}>
                              <Input placeholder="예: 다수의 직접 후기에서 반복 확인된 장점입니다." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ title: '', description: '' })}
                    >
                      검증 포인트 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card> */}

            {/* <Card size="small" title="추천 포인트">
              <Form.List name="featuresJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`추천 포인트 ${field.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="제목"
                              name={[field.name, 'title']}
                              rules={[
                                { required: true, message: '제목을 입력해 주세요.' },
                              ]}
                            >
                              <Input placeholder="예: 다양한 원단 보유" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={16}>
                            <Form.Item label="설명" name={[field.name, 'description']}>
                              <Input placeholder="예: 취향에 맞는 정품 원단을 직접 비교할 수 있습니다." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ title: '', description: '' })}
                    >
                      추천 포인트 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card> */}

            <Card size="small" title="구성/가격">
              <Form.List name="priceItemsJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`구성 ${field.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="구성명"
                              name={[field.name, 'title']}
                              rules={[
                                { required: true, message: '구성명을 입력해 주세요.' },
                              ]}
                            >
                              <Input placeholder="예: 맞춤 예복" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={8}>
                            <Form.Item label="가격" name={[field.name, 'priceText']}>
                              <Input placeholder="예: 1,290,000원 ~" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={8}>
                            <Form.Item label="노출 순서" name={[field.name, 'sortOrder']}>
                              <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                          </Col>

                          <Col xs={24}>
                            <Form.Item label="설명" name={[field.name, 'description']}>
                              <Input placeholder="예: 촬영 + 본식 포함" />
                            </Form.Item>
                          </Col>

                          <Col xs={24}>
                            <Form.Item label="안내 문구" name={[field.name, 'notice']}>
                              <Input placeholder="예: 원단과 구성에 따라 금액이 달라질 수 있습니다." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          title: '',
                          description: '',
                          priceText: '',
                          notice: '',
                          sortOrder: fields.length + 1,
                          isPublished: 1,
                        })
                      }
                    >
                      구성 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card>

            <Card size="small" title="제휴 혜택">
              <Form.List name="promotionBenefitsJson">
                {(vendorFields, { add: addVendor, remove: removeVendor }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {vendorFields.map((vendorField) => (
                      <Card
                        key={vendorField.key}
                        size="small"
                        title={`혜택 그룹 ${vendorField.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => removeVendor(vendorField.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="연결 업체명"
                              name={[vendorField.name, 'vendorName']}
                            >
                              <Input placeholder="예: 사진로라스튜디오" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={12}>
                            <Form.Item
                              label="분야"
                              name={[vendorField.name, 'vendorCategory']}
                            >
                              <Input placeholder="예: 스튜디오" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.List name={[vendorField.name, 'benefits']}>
                          {(
                            benefitFields,
                            { add: addBenefit, remove: removeBenefit }
                          ) => (
                            <Space
                              direction="vertical"
                              size={8}
                              style={{ width: '100%' }}
                            >
                              {benefitFields.map((benefitField) => (
                                <Row gutter={8} key={benefitField.key} align="middle">
                                  <Col xs={24} md={8}>
                                    <Form.Item
                                      label="혜택"
                                      name={[benefitField.name, 'title']}
                                    >
                                      <Input placeholder="예: 22만원 할인" />
                                    </Form.Item>
                                  </Col>

                                  <Col xs={22} md={14}>
                                    <Form.Item
                                      label="조건"
                                      name={[benefitField.name, 'condition']}
                                    >
                                      <Input placeholder="예: 웨딩 촬영 시 가능" />
                                    </Form.Item>
                                  </Col>

                                  <Col xs={2} md={2}>
                                    <Button
                                      danger
                                      type="text"
                                      icon={<MinusCircleOutlined />}
                                      onClick={() => removeBenefit(benefitField.name)}
                                    />
                                  </Col>
                                </Row>
                              ))}

                              <Button
                                type="dashed"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() =>
                                  addBenefit({ title: '', condition: '' })
                                }
                              >
                                혜택 추가
                              </Button>
                            </Space>
                          )}
                        </Form.List>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        addVendor({
                          vendorName: '',
                          vendorCategory: '',
                          benefits: [],
                        })
                      }
                    >
                      혜택 그룹 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card>

            {/* <Card size="small" title="자주 묻는 질문">
              <Form.List name="faqJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`질문 ${field.name + 1}`}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            삭제
                          </Button>
                        }
                      >
                        <Form.Item
                          label="질문"
                          name={[field.name, 'question']}
                          rules={[
                            { required: true, message: '질문을 입력해 주세요.' },
                          ]}
                        >
                          <Input placeholder="예: 제작 기간은 얼마나 걸리나요?" />
                        </Form.Item>

                        <Form.Item label="답변" name={[field.name, 'answer']}>
                          <TextArea rows={3} placeholder="답변을 입력해 주세요." />
                        </Form.Item>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ question: '', answer: '' })}
                    >
                      질문 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card> */}

            <Card size="small" title="방문/예약 안내">
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item label="주소" name="address">
                    <Input placeholder="예: 서울 강남구 청담동 123-4" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="운영시간" name="openingHours">
                    <Input placeholder="예: 월–토 10:00–19:00 / 일요일 휴무" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="주차" name="parkingInfo">
                    <Input placeholder="예: 건물 내 지하 주차장 이용" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="예약 방식" name="reservationMethod">
                    <Input placeholder="예: 네이버 예약 또는 전화 예약" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="상담 방식" name="consultationMethod">
                    <Input placeholder="예: 방문 상담 중심" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="전화번호" name="phone">
                    <Input placeholder="예: 02-0000-0000" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="홈페이지" name="homepageUrl">
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="네이버 지도 ID" name="naverPlaceId">
                    <Input placeholder="https://..." />
                    {naverMapSearchKeyword && (
                      <>
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://map.naver.com/p/search/${encodeURIComponent(naverMapSearchKeyword)}?c=15.00,0,0,0,dh`}
                      >
                        네이버지도로 이동
                      </a>
                      </>
                    )}
                    <p style={{fontSize: 12, color: '#999'}}>https://map.naver.com/p/search/%EA%B9%8C%EC%82%AC%EB%B9%84%ED%86%A0/place/<span style={{color: 'red', fontWeight: 'bold'}}>1939266338</span> 부분을 넣어주세요</p>
                  </Form.Item>
                  
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item label="위도" name="lat">
                    <InputNumber
                      placeholder="37.1234567"
                      style={{ width: '100%' }}
                      controls={false}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item label="경도" name="lon">
                    <InputNumber
                      placeholder="127.1234567"
                      style={{ width: '100%' }}
                      controls={false}
                    />
                  </Form.Item>
                </Col>

                {/* <Col xs={24} md={12}>
                  <Form.Item label="구글 지도" name="googleMapUrl">
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col> */}
              </Row>

              <Divider />

              <Form.List name="noticeJson">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Row gutter={8} key={field.key} align="middle">
                        <Col xs={24} md={8}>
                          <Form.Item label="안내 제목" name={[field.name, 'title']}>
                            <Input placeholder="예: 방문 전 예약 필수" />
                          </Form.Item>
                        </Col>

                        <Col xs={22} md={14}>
                          <Form.Item
                            label="안내 내용"
                            name={[field.name, 'description']}
                          >
                            <Input placeholder="예: 주말 상담은 조기 마감될 수 있습니다." />
                          </Form.Item>
                        </Col>

                        <Col xs={2} md={2}>
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </Col>
                      </Row>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ title: '', description: '' })}
                    >
                      안내 추가
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Card>
          </Form>
        </Space>
      </Space>
    </Card>
  );
}
